import { useLanguage } from "../context/LanguageContext";
import messageTranslations from "../utils/messageContent";
import { useAuth } from "../context/authContext";

const useContestService = () => {
  const { language } = useLanguage();
  const { fetchUser } = useAuth();
  const t = messageTranslations[language] || messageTranslations["de"];

  // ============= CONTEST MANAGEMENT =============

  const createContest = async (payload) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contest/contests`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            t.contest?.createError ||
            "Gewinnspiel-Erstellung fehlgeschlagen."
        );
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.data,
        message: data.message,
      };
    } catch (error) {
      console.error("Create Contest Error:", error.message);
      throw error;
    }
  };

  const getContests = async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.status) queryParams.append("status", filters.status);
      if (filters.page) queryParams.append("page", filters.page);
      if (filters.limit) queryParams.append("limit", filters.limit);

      const queryString = queryParams.toString();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/contest/contests${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          t.contest?.loadListError || "Fehler beim Laden der Gewinnspiele."
        );
      }

      const data = await response.json();
      await fetchUser();
      return {
        contests: data.data,
        pagination: data.pagination,
      };
    } catch (error) {
      console.error("Get Contests Error:", error.message);
      return { contests: [], pagination: null };
    }
  };

  const getContestById = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contest/contests/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            t.contest?.loadSingleError ||
            "Fehler beim Laden des Gewinnspiels."
        );
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("Get Contest by ID Error:", error.message);
      return null;
    }
  };

  const updateContest = async (id, payload) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contest/contests/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            t.contest?.updateError ||
            "Fehler beim Aktualisieren des Gewinnspiels."
        );
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.data,
        message: data.message,
      };
    } catch (error) {
      console.error("Update Contest Error:", error.message);
      throw error;
    }
  };

  const deleteContest = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contest/contests/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            t.contest?.deleteError ||
            "Fehler beim Löschen des Gewinnspiels."
        );
      }

      const data = await response.json();
      return {
        success: data.success,
        mode: data.mode,
        message: data.message,
      };
    } catch (error) {
      console.error("Delete Contest Error:", error.message);
      throw error;
    }
  };

  // ============= CONTEST DRAWING =============

  const drawWinners = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contest/contests/${id}/draw`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            t.contest?.drawError ||
            "Fehler beim Ziehen der Gewinner."
        );
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("Draw Winners Error:", error.message);
      throw error;
    }
  };

  const getContestWinners = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contest/contests/${id}/winners`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            t.contest?.loadWinnersError ||
            "Fehler beim Laden der Gewinner."
        );
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("Get Contest Winners Error:", error.message);
      return [];
    }
  };

  // ============= CONTEST PARTICIPANTS =============

  const getContestParticipants = async (id, filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.page) queryParams.append("page", filters.page);
      if (filters.limit) queryParams.append("limit", filters.limit);

      const queryString = queryParams.toString();
      const url = `${
        process.env.REACT_APP_BACKEND_URL
      }/api/contest/contests/${id}/participants${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            t.contest?.loadParticipantsError ||
            "Fehler beim Laden der Teilnehmer."
        );
      }

      const data = await response.json();
      return {
        participants: data.data.participants,
        totalCount: data.data.totalCount,
        uniqueParticipants: data.data.uniqueParticipants,
        allowMultipleEntries: data.data.allowMultipleEntries,
        requiresEmail: data.data.requiresEmail,
        pagination: data.pagination,
      };
    } catch (error) {
      console.error("Get Contest Participants Error:", error.message);
      return {
        participants: [],
        totalCount: 0,
        uniqueParticipants: 0,
        allowMultipleEntries: false,
        requiresEmail: false,
        pagination: null,
      };
    }
  };

  // ============= WINNER MANAGEMENT - ENHANCED =============

  const updateWinnerStatus = async (winnerId, status) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contest/winners/${winnerId}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({ notificationStatus: status }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            t.contest?.updateWinnerError ||
            "Fehler beim Aktualisieren des Gewinner-Status."
        );
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("Update Winner Status Error:", error.message);
      throw error;
    }
  };

  // ✅ NEU: Winner-Notizen aktualisieren
  const updateWinnerNotes = async (winnerId, notes) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contest/winners/${winnerId}/notes`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({ notes }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            t.contest?.updateNotesError ||
            "Fehler beim Aktualisieren der Notizen."
        );
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("Update Winner Notes Error:", error.message);
      throw error;
    }
  };

  // ✅ NEU: Alle Gewinner eines Users laden (für Dashboard)
  const getAllWinners = async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.status) queryParams.append("status", filters.status);
      if (filters.page) queryParams.append("page", filters.page);
      if (filters.limit) queryParams.append("limit", filters.limit);

      const queryString = queryParams.toString();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/contest/winners${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            t.contest?.loadAllWinnersError ||
            "Fehler beim Laden aller Gewinner."
        );
      }

      const data = await response.json();
      return {
        winners: data.data,
        pagination: data.pagination,
      };
    } catch (error) {
      console.error("Get All Winners Error:", error.message);
      return { winners: [], pagination: null };
    }
  };

  // ✅ ERWEITERT: Email senden mit besserem Error-Handling
  const sendWinnerEmail = async (winnerId, customMessage = "") => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contest/winners/${winnerId}/send-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({ customMessage }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            t.contest?.sendEmailError ||
            "Fehler beim Senden der E-Mail."
        );
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("Send Winner Email Error:", error.message);
      throw error;
    }
  };

  // ✅ NEU: Winner-Validierung
  const validateWinnerStatus = (currentStatus, newStatus) => {
    const statusFlow = {
      pending: ["contacted", "voucher_sent", "expired"],
      contacted: ["voucher_sent", "completed", "expired"],
      voucher_sent: ["completed", "expired"],
      completed: [], // End state
      expired: [], // End state
    };

    return statusFlow[currentStatus]?.includes(newStatus) || false;
  };

  // ✅ NEU: Winner-Status-Text für UI
  const getWinnerStatusText = (status) => {
    const statusTexts = {
      pending: t.winnerStatus?.pending || "Ausstehend",
      contacted: t.winnerStatus?.contacted || "Kontaktiert",
      voucher_sent: t.winnerStatus?.voucherSent || "Gutschein gesendet",
      completed: t.winnerStatus?.completed || "Abgeschlossen",
      expired: t.winnerStatus?.expired || "Abgelaufen",
    };
    return statusTexts[status] || status;
  };

  // ✅ NEU: Winner-Status-Farben für UI
  const getWinnerStatusColor = (status) => {
    const statusColors = {
      pending: "#fa8c16", // Orange
      contacted: "#1890ff", // Blue
      voucher_sent: "#52c41a", // Green
      completed: "#722ed1", // Purple
      expired: "#f5222d", // Red
    };
    return statusColors[status] || "#666";
  };

  // ============= UTILITY FUNCTIONS =============

  const getContestStats = async (id) => {
    try {
      // Gleichzeitig Contest-Details und Teilnehmer laden
      const [contestData, participantsData] = await Promise.all([
        getContestById(id),
        getContestParticipants(id, { limit: 1 }), // Nur für Stats, nicht alle laden
      ]);

      if (!contestData) return null;

      return {
        contest: contestData,
        participantCount: participantsData.totalCount,
        uniqueParticipants: participantsData.uniqueParticipants,
        winnerCount: contestData.winnerCount || 0,
        status: contestData.status,
        isDrawable: contestData.status === "ended",
        isEditable: ["upcoming", "active"].includes(contestData.status),
      };
    } catch (error) {
      console.error("Get Contest Stats Error:", error.message);
      return null;
    }
  };

  const validateContestData = (contestData) => {
    const errors = [];

    if (!contestData.title?.trim()) {
      errors.push("Titel ist erforderlich");
    }

    if (!contestData.description?.trim()) {
      errors.push("Beschreibung ist erforderlich");
    }

    if (!contestData.startDate) {
      errors.push("Startdatum ist erforderlich");
    }

    if (!contestData.endDate) {
      errors.push("Enddatum ist erforderlich");
    }

    if (contestData.startDate && contestData.endDate) {
      if (new Date(contestData.endDate) <= new Date(contestData.startDate)) {
        errors.push("Enddatum muss nach dem Startdatum liegen");
      }
    }

    if (!contestData.prizes || contestData.prizes.length === 0) {
      errors.push("Mindestens ein Gewinn muss definiert werden");
    }

    if (contestData.prizes) {
      const places = contestData.prizes.map((p) => p.place);
      const uniquePlaces = [...new Set(places)];
      if (places.length !== uniquePlaces.length) {
        errors.push("Jeder Platz darf nur einmal vergeben werden");
      }
    }

    return errors;
  };

  const formatContestDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getContestStatusText = (status) => {
    const statusTexts = {
      upcoming: "Bevorstehend",
      active: "Aktiv",
      ended: "Beendet",
      drawn: "Ausgelost",
      archived: "Archiviert",
    };
    return statusTexts[status] || status;
  };

  const getContestStatusColor = (status) => {
    const statusColors = {
      upcoming: "#1890ff",
      active: "#52c41a",
      ended: "#fa8c16",
      drawn: "#722ed1",
      archived: "#666",
    };
    return statusColors[status] || "#666";
  };

  // ============= BULK OPERATIONS =============

  const exportContestData = async (id, format = "csv") => {
    try {
      const [contest, participants, winners] = await Promise.all([
        getContestById(id),
        getContestParticipants(id, { limit: 1000 }),
        getContestWinners(id),
      ]);

      if (!contest) return null;

      const exportData = {
        contest,
        participants: participants.participants,
        winners,
        exportedAt: new Date().toISOString(),
        format,
      };

      // Hier könnte eine CSV/Excel-Export-Logik implementiert werden
      return exportData;
    } catch (error) {
      console.error("Export Contest Data Error:", error.message);
      return null;
    }
  };

  return {
    // Contest Management
    createContest,
    getContests,
    getContestById,
    updateContest,
    deleteContest,

    // Contest Drawing
    drawWinners,
    getContestWinners,

    // Participants
    getContestParticipants,

    // Winner Management - ENHANCED
    updateWinnerStatus,
    updateWinnerNotes, // ✅ NEW
    getAllWinners, // ✅ NEW
    sendWinnerEmail,

    // Winner Utility Functions - NEW
    validateWinnerStatus, // ✅ NEW
    getWinnerStatusText, // ✅ NEW
    getWinnerStatusColor, // ✅ NEW

    // Utility Functions
    getContestStats,
    validateContestData,
    formatContestDate,
    getContestStatusText,
    getContestStatusColor,
    exportContestData,
  };
};

export default useContestService;
